import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE } from "../lib/site";

/**
 * Reusable SEO component that injects route-specific <title>, meta description,
 * canonical URL, robots directive, and Open Graph / Twitter tags.
 *
 * Props:
 *  - title:        Page-specific title (without brand suffix). If omitted, SITE.name is used.
 *  - description:  Meta description (aim for 150-160 chars).
 *  - noindex:      When true, emits <meta name="robots" content="noindex, nofollow">.
 *  - image:        Optional absolute URL for og:image / twitter:image.
 *  - type:         Open Graph type (default "website").
 */
export default function Seo({
  title,
  description,
  noindex = false,
  image,
  type = "website",
}) {
  const location = useLocation();
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "";
  const canonical = `${origin}${location.pathname}`;
  const fullTitle = title ? `${title} | ${SITE.name}` : SITE.name;
  const ogImage = image || `${origin}/favicon.svg`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE.name} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={fullTitle} />
      {description && (
        <meta name="twitter:description" content={description} />
      )}
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
